import { DETAIL_TYPE } from '@/components/insights/insights-constants';
import logger from '@/logger';
import api from '@/services/api';

export const detailKey = (type, code) =>
  type === DETAIL_TYPE.polling ? `${type}` : `${type}|${code}`;

const DEFAULT_FILTER_ID = 0;

let session = 0;

const initialState = () => ({
  status: 'idle',
  marketId: null,
  loggedIn: false,
  detailTypes: [],
  signInRequiredTypes: [],
  filters: [],
  regions: [],
  selectedCode: '',
  details: {},
});

export default {
  namespaced: true,
  state: initialState,
  getters: {
    defaultRegion: (state) => state.regions.find((r) => r.filterId === DEFAULT_FILTER_ID) || null,
    selectedRegion: (state, getters) =>
      state.regions.find((r) => r.detailObjectCode === state.selectedCode) || getters.defaultRegion,
    detailFor: (state) => (type, code) => state.details[detailKey(type, code)] || null,
  },
  mutations: {
    reset(state) {
      Object.assign(state, initialState());
    },
    setStatus(state, status) {
      state.status = status;
    },
    setRoot(
      state,
      { marketId, loggedIn, detailTypes, signInRequiredTypes = [], filters, regions },
    ) {
      state.marketId = marketId;
      state.loggedIn = loggedIn;
      state.detailTypes = detailTypes;
      state.signInRequiredTypes = signInRequiredTypes;
      state.filters = filters;
      state.regions = regions;
    },
    setSelectedCode(state, code) {
      state.selectedCode = code;
    },
    setDetail(state, { key, status, groups, throttled = false }) {
      state.details = {
        ...state.details,
        [key]: {
          status,
          throttled,
          groups: groups || (state.details[key] && state.details[key].groups) || [],
        },
      };
    },
  },
  actions: {
    async loadRoot({ commit, state, dispatch, rootGetters }, { marketId, force = false }) {
      const loggedIn = !!rootGetters['user/isLoggedIn'];
      if (
        !force &&
        state.marketId === marketId &&
        state.loggedIn === loggedIn &&
        ['loading', 'ready'].includes(state.status)
      ) {
        return;
      }
      session += 1;
      const current = session;
      commit('reset');
      commit('setStatus', 'loading');
      commit('setRoot', { marketId, loggedIn, detailTypes: [], filters: [], regions: [] });
      try {
        const { data } = await api.market.getInsightsRootObjects(marketId);
        if (session !== current) return;
        const regions = (data && data.regions) || [];
        const detailTypes = (data && data.availableDetailTypes) || [];
        const defaultRegion = regions.find((r) => r.filterId === DEFAULT_FILTER_ID);
        const canShowElectorate = !!defaultRegion && detailTypes.includes(DETAIL_TYPE.demographics);
        const canShowPolls = detailTypes.includes(DETAIL_TYPE.polling);
        if (!canShowElectorate && !canShowPolls) {
          commit('setStatus', 'hidden');
          return;
        }
        commit('setRoot', {
          marketId,
          loggedIn,
          detailTypes,
          signInRequiredTypes: data.signInRequiredDetailTypes || [],
          filters: data.regionFilters || [],
          regions,
        });
        commit('setSelectedCode', (defaultRegion && defaultRegion.detailObjectCode) || '');
        if (defaultRegion && defaultRegion.initialDetailObject) {
          dispatch('cacheDetail', {
            type: DETAIL_TYPE.demographics,
            code: defaultRegion.detailObjectCode,
            data: defaultRegion.initialDetailObject,
          });
        }
        commit('setStatus', 'ready');
      } catch (error) {
        logger.error('Insights panel root request failed', error);
        if (session === current) commit('setStatus', 'hidden');
      }
    },
    selectRegion({ commit }, code) {
      commit('setSelectedCode', code);
    },
    cacheDetail({ commit }, { type, code, data }) {
      commit('setDetail', {
        key: detailKey(type, code),
        status: 'ready',
        groups: (data && data.detailObjectGroups) || [],
      });
    },
    async loadDetail({ commit, state, dispatch }, { type, code }) {
      const key = detailKey(type, code);
      const cached = state.details[key];
      if (cached && cached.status !== 'error') return;
      const { marketId } = state;
      const current = session;
      commit('setDetail', { key, status: 'loading', groups: [] });
      try {
        const { data } = await api.market.getInsightsDetailObject({ marketId, type, code });
        if (session !== current) return;
        dispatch('cacheDetail', { type, code, data });
      } catch (error) {
        logger.error('Insights panel detail request failed', error);
        const throttled = !!(error && error.response && error.response.status === 429);
        if (session === current) {
          commit('setDetail', { key, status: 'error', groups: [], throttled });
        }
      }
    },
  },
};
